import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { AuthPage } from '../components/auth/AuthPage';
import { LoginForm } from '../components/auth/LoginForm';
import { SignUpForm } from '../components/auth/SignUpForm';
import { ForgotPasswordForm } from '../components/auth/ForgotPasswordForm';
import { EmailConfirmation } from '../components/auth/EmailConfirmation';
import { ResetPassword } from '../components/auth/ResetPassword';
import { OrganizationSetup } from '../components/organization/OrganizationSetup';
import { ErrorBoundary } from '../components/shared/ErrorBoundary';
import { AppLayout } from '../components/layout/AppLayout';

/**
 * LoadingScreen - Shared loading screen component used across route guards
 */
function LoadingScreen() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#5D1828] via-[#7A2235] to-[#8B2E3F] flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-white font-medium">Cargando...</p>
      </div>
    </div>
  );
}

/**
 * ProtectedRoute - Requires authentication and organization
 * Redirects to login if not authenticated, org setup if no organization
 */
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { currentOrg, organizations, loading: orgLoading } = useOrganization();

  // Show loading state while checking auth
  if (authLoading || orgLoading) {
    return <LoadingScreen />;
  }

  // Not authenticated - redirect to login
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Authenticated but no organization - redirect to organization setup
  if (!currentOrg && organizations.length === 0) {
    return <Navigate to="/organization-setup" replace />;
  }

  // If organizations exist but currentOrg not set yet, keep loading
  if (!currentOrg && organizations.length > 0) {
    return <LoadingScreen />;
  }

  // Authenticated and has organization - show protected content
  return <>{children}</>;
}

/**
 * PublicRoute - Accessible without authentication
 * Redirects to app if already logged in with organization
 */
export function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { currentOrg, organizations, loading: orgLoading } = useOrganization();

  // Show loading while checking auth
  if (authLoading || orgLoading) {
    return <LoadingScreen />;
  }

  // If user is logged in and has org, redirect to app
  if (user && currentOrg) {
    return <Navigate to="/app" replace />;
  }

  // If user is logged in but currentOrg not set yet and has orgs, keep loading
  if (user && !currentOrg && organizations.length > 0) {
    return <LoadingScreen />;
  }

  // If user is logged in but no org, redirect to org setup
  if (user && !currentOrg && organizations.length === 0) {
    return <Navigate to="/organization-setup" replace />;
  }

  // Not logged in - show public route
  return <>{children}</>;
}

/**
 * OrgSetupRoute - Requires authentication but no organization
 * Redirects to app if user already has organization
 */
export function OrgSetupRoute({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { currentOrg, organizations, loading: orgLoading } = useOrganization();

  // Show loading while checking auth
  if (authLoading || orgLoading) {
    return <LoadingScreen />;
  }

  // Not authenticated - redirect to login
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // If user has org, redirect to app (don't show org setup)
  if (currentOrg) {
    return <Navigate to="/app" replace />;
  }

  // If organizations exist but currentOrg not set yet, keep loading
  if (!currentOrg && organizations.length > 0) {
    return <LoadingScreen />;
  }

  // User has no org - show organization setup
  return <>{children}</>;
}

/**
 * AppRoutes - Main application route configuration
 */
export function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={
        <PublicRoute>
          <AuthPage><LoginForm /></AuthPage>
        </PublicRoute>
      } />
      <Route path="/signup" element={
        <PublicRoute>
          <AuthPage><SignUpForm /></AuthPage>
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={
        <PublicRoute>
          <AuthPage><ForgotPasswordForm /></AuthPage>
        </PublicRoute>
      } />

      {/* Email confirmation and password reset (accessible without auth) */}
      <Route path="/email-confirmation" element={
        <AuthPage><EmailConfirmation /></AuthPage>
      } />
      <Route path="/reset-password" element={
        <AuthPage><ResetPassword /></AuthPage>
      } />

      {/* Organization setup (requires auth but no org) */}
      <Route path="/organization-setup" element={
        <OrgSetupRoute>
          <OrganizationSetup />
        </OrgSetupRoute>
      } />

      {/* Main app (requires auth and org) */}
      <Route path="/app" element={
        <ProtectedRoute>
          <ErrorBoundary level="page">
            <AppLayout />
          </ErrorBoundary>
        </ProtectedRoute>
      } />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
